import { getPinUnlockLocation, useAuth } from '~/entities/auth'
import { useNotificationStore } from '~/shared/lib'

const APOLLO_CLIENT_ERROR_MESSAGE_MAP: Readonly<Record<string, string>> = {
  'Failed to fetch':
    'Не удалось связаться с сервером. Проверьте подключение к интернету и попробуйте снова.',
}
const DEFAULT_APOLLO_CLIENT_ERROR_MESSAGE = 'Не удалось загрузить данные из-за нестабильного интернета. Попробуйте позже.'

const CYRILLIC_RE = /[\u0400-\u04FF]/

function resolveApolloClientUserFacingMessage(message: string | undefined | null): string {
  const raw = (message ?? '').trim()
  if (!raw) {
    return DEFAULT_APOLLO_CLIENT_ERROR_MESSAGE
  }
  return APOLLO_CLIENT_ERROR_MESSAGE_MAP[raw] ?? (CYRILLIC_RE.test(raw) ? raw : DEFAULT_APOLLO_CLIENT_ERROR_MESSAGE)
}

interface IError {
  statusCode: string | number
  serviceName: string
  message: string
  details?: { description?: string }
  traceId?: string
}

export default defineNuxtPlugin((nuxtApp) => {
  const {
    isAuthenticated,
    logout,
    ensureValidAccessToken,
    refreshToken,
    shouldRequirePinUnlock,
  } = useAuth()
  const { notify } = useNotificationStore()
  let authRecoveryInFlight: Promise<boolean> | null = null

  // @ts-expect-error apollo:error has in lib
  nuxtApp.hook('apollo:error', ({ graphQLErrors, operation, networkError }) => {
    const qlErrors = graphQLErrors as IError[] | undefined
    const nwError = networkError as IError

    const errors = qlErrors || [nwError]

    const publicOperations = [
      'refreshToken',
      'loginByCode',
      'presignedGetUrl',
      'updateUserOnlineStatus',
      'project',
      'projectPublic',
      'getLandingTemplates',
      'projectModuleAccessSettingsBySegment',
      'upsertProjectModuleAccessSettings',
      'getMsProjectModuleAccessPermissions',
      'currentUserProjectModuleCapabilities',
      'currentUserProjectModuleCapabilitiesBatch',
      'currentUserGlobalModuleCapabilities',
    ]
    if (publicOperations.includes(operation.operationName)) {
      return
    }

    if (import.meta.client) {
      void (async () => {
        const hasAuthError = errors.some(({ message, statusCode }) =>
          ['UNAUTHENTICATED', 401].includes(statusCode) || message === 'Token is NOT valid',
        )

        if (hasAuthError) {
          if (!isAuthenticated.value && !refreshToken.value) {
            return
          }

          if (!authRecoveryInFlight) {
            authRecoveryInFlight = ensureValidAccessToken().finally(() => {
              authRecoveryInFlight = null
            })
          }

          const recovered = await authRecoveryInFlight

          if (!recovered) {
            if (shouldRequirePinUnlock()) {
              await navigateTo(getPinUnlockLocation({
                fullPath: useRoute().fullPath,
              }), { replace: true })
            }
            else {
              logout()
            }
          }

          return
        }

        errors.forEach(({ message, details, traceId }) => {
          notify({
            message: details?.description?.trim() || resolveApolloClientUserFacingMessage(message),
            hint: traceId,
            type: 'error',
            delay: 8000,
          })
        })
      })()
    }
  })
})
